import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Sparkles, Rss, Plus, Trash2, RefreshCw, FileText, GitBranch, Loader2, Shield, KeyRound, Save, MessageSquare, CreditCard, Eye, EyeOff, Mail, Tv } from "lucide-react";
import { EmailPreviewModal } from "../components/EmailPreviewModal";
import { VideoAdmin } from "../components/admin/VideoAdmin";

const TABS = [
  { id: "articles", label: "Artikel", icon: FileText },
  { id: "repos", label: "Repository", icon: GitBranch },
  { id: "harvester", label: "AI + RSS", icon: Sparkles },
  { id: "comments", label: "Komentar", icon: MessageSquare },
  { id: "videos", label: "Video", icon: Tv },
  { id: "payments", label: "Pembayaran", icon: CreditCard },
  { id: "security", label: "Keamanan", icon: Shield },
];

export default function Admin() {
  const token = localStorage.getItem("noxai_token");
  const [tab, setTab] = useState("articles");
  const [articles, setArticles] = useState([]);
  const [repos, setRepos] = useState([]);
  const [busy, setBusy] = useState(false);

  // article form
  const [aForm, setAForm] = useState({ title: "", excerpt: "", content: "", category: "AI", image_url: "", tags: "" });
  // repo form
  const [rForm, setRForm] = useState({ name: "", full_name: "", description: "", category: "Framework", language: "", install_command: "", tags: "" });
  // AI form
  const [aiTopic, setAiTopic] = useState("");
  const [aiCategory, setAiCategory] = useState("AI");
  const [rssLimit, setRssLimit] = useState(5);
  // security
  const [pwForm, setPwForm] = useState({ current: "", next: "", confirm: "" });
  const [secSettings, setSecSettings] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);
  // comments & payments
  const [comments, setComments] = useState([]);
  const [paySettings, setPaySettings] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [digests, setDigests] = useState([]);
  const [emailStatus, setEmailStatus] = useState(null);
  const [testEmail, setTestEmail] = useState("");
  const [preview, setPreview] = useState(null);

  const openPreview = async (params) => {
    try {
      const r = await api.get("/admin/email-preview", { params });
      setPreview(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal memuat pratinjau");
    }
  };

  const load = () => {
    api.get("/articles?limit=100").then((r) => setArticles(r.data));
    api.get("/repositories?limit=200").then((r) => setRepos(r.data));
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (tab === "security") {
      api.get("/admin/security-settings").then((r) => setSecSettings(r.data)).catch(() => {});
      api.get("/status/last-update").then((r) => setSyncStatus(r.data)).catch(() => {});
    }
    if (tab === "comments") {
      api.get("/admin/comments").then((r) => setComments(r.data)).catch(() => {});
    }
    if (tab === "payments") {
      api.get("/admin/payment-settings").then((r) => setPaySettings(r.data)).catch(() => {});
      api.get("/admin/transactions").then((r) => setTransactions(r.data)).catch(() => {});
      api.get("/admin/subscribers").then((r) => setSubscribers(r.data)).catch(() => {});
      api.get("/newsletter/digests").then((r) => setDigests(r.data)).catch(() => {});
      api.get("/admin/email-status").then((r) => setEmailStatus(r.data)).catch(() => {});
    }
  }, [tab]);

  if (!token) return <Navigate to="/login" />;

  const createArticle = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/articles", {
        ...aForm,
        tags: aForm.tags.split(",").map((t) => t.trim()).filter(Boolean),
      });
      toast.success("Artikel dibuat");
      setAForm({ title: "", excerpt: "", content: "", category: "AI", image_url: "", tags: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal");
    }
    setBusy(false);
  };

  const deleteArticle = async (id) => {
    if (!window.confirm("Hapus artikel ini?")) return;
    await api.delete(`/articles/${id}`);
    toast.success("Dihapus");
    load();
  };

  const createRepo = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/repositories", {
        ...rForm,
        github_url: `https://github.com/${rForm.full_name}`,
        tags: rForm.tags.split(",").map((t) => t.trim()).filter(Boolean),
      });
      toast.success("Repository ditambahkan (stars di-sync)");
      setRForm({ name: "", full_name: "", description: "", category: "Framework", language: "", install_command: "", tags: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal");
    }
    setBusy(false);
  };

  const deleteRepo = async (id) => {
    if (!window.confirm("Hapus repository?")) return;
    await api.delete(`/repositories/${id}`);
    toast.success("Dihapus");
    load();
  };

  const syncRepo = async (id) => {
    setBusy(true);
    try {
      await api.post(`/repositories/${id}/sync`);
      toast.success("Sync sukses");
      load();
    } catch (e) {
      toast.error("Sync gagal");
    }
    setBusy(false);
  };

  const syncAll = async () => {
    setBusy(true);
    toast.info("Sync semua repository...");
    try {
      const r = await api.post("/repositories/sync-all");
      toast.success(`${r.data.updated} repo diperbarui`);
      load();
    } catch (e) {
      toast.error("Sync-all gagal");
    }
    setBusy(false);
  };

  const generateAI = async (e) => {
    e.preventDefault();
    if (!aiTopic.trim()) return;
    setBusy(true);
    toast.info("AI sedang menulis artikel...");
    try {
      await api.post("/ai/generate-article", { topic: aiTopic, category: aiCategory });
      toast.success("Artikel AI dibuat");
      setAiTopic("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI gagal");
    }
    setBusy(false);
  };

  const fetchRSS = async () => {
    setBusy(true);
    toast.info("Mengambil RSS feed...");
    try {
      const r = await api.post("/rss/fetch", { limit: parseInt(rssLimit) || 5 });
      toast.success(`${r.data.inserted} artikel baru dari RSS`);
      load();
    } catch (e) {
      toast.error("RSS fetch gagal");
    }
    setBusy(false);
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwForm.next !== pwForm.confirm) {
      toast.error("Konfirmasi password tidak cocok");
      return;
    }
    setBusy(true);
    try {
      await api.post("/admin/change-password", { current_password: pwForm.current, new_password: pwForm.next });
      toast.success("Password berhasil diubah");
      setPwForm({ current: "", next: "", confirm: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal mengubah password");
    }
    setBusy(false);
  };

  const saveSecuritySettings = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.put("/admin/security-settings", secSettings);
      setSecSettings(r.data);
      toast.success("Pengaturan keamanan disimpan");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menyimpan");
    }
    setBusy(false);
  };

  const toggleCommentStatus = async (c) => {
    try {
      await api.patch(`/admin/comments/${c.id}`, { status: c.status === "visible" ? "hidden" : "visible" });
      const r = await api.get("/admin/comments");
      setComments(r.data);
      toast.success("Status komentar diperbarui");
    } catch {
      toast.error("Gagal memperbarui");
    }
  };

  const deleteComment = async (id) => {
    if (!window.confirm("Hapus komentar permanen?")) return;
    try {
      await api.delete(`/comments/${id}`);
      setComments(comments.filter((c) => c.id !== id));
      toast.success("Komentar dihapus");
    } catch {
      toast.error("Gagal menghapus");
    }
  };

  const savePaymentSettings = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.put("/admin/payment-settings", paySettings);
      setPaySettings(r.data);
      toast.success("Pengaturan pembayaran disimpan");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal menyimpan");
    }
    setBusy(false);
  };

  const generateDigest = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/generate-digest");
      toast.success(`Digest dibuat: ${r.data.title}`);
      setDigests([r.data, ...digests]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal membuat digest");
    }
    setBusy(false);
  };

  const sendDigest = async (id) => {
    if (!window.confirm("Kirim digest ini via email ke semua pelanggan aktif?")) return;
    setBusy(true);
    try {
      const r = await api.post(`/admin/digests/${id}/send`);
      toast.success(`Terkirim ke ${r.data.sent}/${r.data.total} pelanggan${r.data.failed.length ? `, gagal: ${r.data.failed.length}` : ""}`);
      if (r.data.failed[0]) toast.error(r.data.failed[0].error);
      api.get("/newsletter/digests").then((res) => setDigests(res.data));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal mengirim digest");
    }
    setBusy(false);
  };

  const sendTestEmail = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/test-email", { to: testEmail });
      toast.success(`Email tes terkirim ke ${testEmail}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal mengirim email tes");
    }
    setBusy(false);
  };

  const notifyLatest = async () => {
    if (!window.confirm("Kirim notifikasi email artikel terbaru (yang belum pernah dikirim) ke semua pelanggan aktif?")) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/notify-latest-articles", { limit: 5 });
      toast.success(`${r.data.articles} artikel dikirim ke ${r.data.sent}/${r.data.total} pelanggan${r.data.failed.length ? `, gagal: ${r.data.failed.length}` : ""}`);
      if (r.data.failed[0]) toast.error(r.data.failed[0].error);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal mengirim notifikasi");
    }
    setBusy(false);
  };

  const saveEmailSettings = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.put("/admin/payment-settings", { site_url: paySettings.site_url, sender_email: paySettings.sender_email });
      setPaySettings(r.data);
      api.get("/admin/email-status").then((res) => setEmailStatus(res.data));
      toast.success("Pengaturan email disimpan");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal menyimpan");
    }
    setBusy(false);
  };

  const toggleSetting = async (key, value, label) => {
    const r = await api.put("/admin/payment-settings", { [key]: value });
    setPaySettings(r.data);
    toast.success(`${label} ${value ? "aktif" : "nonaktif"}`);
  };

  const reconcileQris = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/payments/qris/reconcile");
      toast.success(`${r.data.checked} transaksi QRIS pending dicek ulang`);
      api.get("/admin/transactions").then((res) => setTransactions(res.data));
      api.get("/admin/subscribers").then((res) => setSubscribers(res.data));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal rekonsiliasi");
    }
    setBusy(false);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 relative z-10">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="chip chip-cyan mb-3">Admin Console</div>
          <h1 className="font-heading text-4xl font-black">Command Center</h1>
        </div>
      </div>

      <div className="flex gap-2 mb-8 flex-wrap">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`btn-ghost ${tab === t.id ? "!border-cyan1 !text-cyan1" : ""}`}
            data-testid={`admin-tab-${t.id}`}
          >
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "articles" && (
        <div className="grid lg:grid-cols-3 gap-6">
          <form onSubmit={createArticle} className="card p-6 space-y-3 lg:col-span-1" data-testid="article-form">
            <h2 className="font-heading text-xl font-bold flex items-center gap-2"><Plus className="w-5 h-5 text-cyan1" /> Buat Artikel</h2>
            <input required placeholder="Judul" value={aForm.title} onChange={(e) => setAForm({ ...aForm, title: e.target.value })} className="w-full bg-void border border-white/10 rounded-lg h-10 px-3 focus:border-cyan1/50 outline-none" data-testid="article-title-input" />
            <select value={aForm.category} onChange={(e) => setAForm({ ...aForm, category: e.target.value })} className="w-full bg-void border border-white/10 rounded-lg h-10 px-3 outline-none">
              <option>AI</option><option>Robotics</option><option>LLM</option><option>Software</option>
            </select>
            <input placeholder="Image URL (opsional)" value={aForm.image_url} onChange={(e) => setAForm({ ...aForm, image_url: e.target.value })} className="w-full bg-void border border-white/10 rounded-lg h-10 px-3 outline-none" />
            <input placeholder="Tags (comma separated)" value={aForm.tags} onChange={(e) => setAForm({ ...aForm, tags: e.target.value })} className="w-full bg-void border border-white/10 rounded-lg h-10 px-3 outline-none" />
            <textarea required placeholder="Excerpt (1 kalimat)" value={aForm.excerpt} onChange={(e) => setAForm({ ...aForm, excerpt: e.target.value })} rows={2} className="w-full bg-void border border-white/10 rounded-lg p-3 outline-none" data-testid="article-excerpt-input" />
            <textarea required placeholder="Konten (Markdown)" value={aForm.content} onChange={(e) => setAForm({ ...aForm, content: e.target.value })} rows={6} className="w-full bg-void border border-white/10 rounded-lg p-3 outline-none font-mono text-sm" data-testid="article-content-input" />
            <button disabled={busy} className="btn-primary w-full justify-center" data-testid="article-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Simpan
            </button>
          </form>
          <div className="lg:col-span-2 space-y-3">
            <h2 className="font-heading text-xl font-bold">Semua Artikel ({articles.length})</h2>
            {articles.map((a) => (
              <div key={a.id} className="card p-4 flex items-center gap-3" data-testid={`admin-article-${a.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex gap-2 flex-wrap">
                    <span className="chip chip-cyan text-[10px]">{a.category}</span>
                    <span className="chip text-[10px]">{a.source}</span>
                  </div>
                  <h3 className="font-heading font-bold mt-1 truncate">{a.title}</h3>
                </div>
                <button onClick={() => deleteArticle(a.id)} className="btn-ghost hover:!border-red-500 hover:!text-red-400" data-testid={`delete-article-${a.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "repos" && (
        <div className="grid lg:grid-cols-3 gap-6">
          <form onSubmit={createRepo} className="card p-6 space-y-3 lg:col-span-1" data-testid="repo-form">
            <h2 className="font-heading text-xl font-bold flex items-center gap-2"><Plus className="w-5 h-5 text-magenta1" /> Tambah Repository</h2>
            <input required placeholder="Nama (e.g. Ollama)" value={rForm.name} onChange={(e) => setRForm({ ...rForm, name: e.target.value })} className="w-full bg-void border border-white/10 rounded-lg h-10 px-3 outline-none" />
            <input required placeholder="owner/repo (e.g. ollama/ollama)" value={rForm.full_name} onChange={(e) => setRForm({ ...rForm, full_name: e.target.value })} className="w-full bg-void border border-white/10 rounded-lg h-10 px-3 outline-none font-mono text-sm" data-testid="repo-fullname" />
            <select value={rForm.category} onChange={(e) => setRForm({ ...rForm, category: e.target.value })} className="w-full bg-void border border-white/10 rounded-lg h-10 px-3 outline-none">
              <option>LLM Runner</option><option>Framework</option><option>Autonomous Agent</option><option>MCP</option>
            </select>
            <input placeholder="Bahasa (Python, Go, ...)" value={rForm.language} onChange={(e) => setRForm({ ...rForm, language: e.target.value })} className="w-full bg-void border border-white/10 rounded-lg h-10 px-3 outline-none" />
            <input placeholder="Install command" value={rForm.install_command} onChange={(e) => setRForm({ ...rForm, install_command: e.target.value })} className="w-full bg-void border border-white/10 rounded-lg h-10 px-3 outline-none font-mono text-xs" />
            <input placeholder="Tags (comma)" value={rForm.tags} onChange={(e) => setRForm({ ...rForm, tags: e.target.value })} className="w-full bg-void border border-white/10 rounded-lg h-10 px-3 outline-none" />
            <textarea required placeholder="Deskripsi" value={rForm.description} onChange={(e) => setRForm({ ...rForm, description: e.target.value })} rows={3} className="w-full bg-void border border-white/10 rounded-lg p-3 outline-none" />
            <button disabled={busy} className="btn-primary w-full justify-center" data-testid="repo-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Tambah &amp; Sync
            </button>
            <button type="button" disabled={busy} onClick={syncAll} className="btn-ghost w-full justify-center" data-testid="sync-all-repos">
              <RefreshCw className="w-4 h-4" /> Sync Semua Stars
            </button>
          </form>
          <div className="lg:col-span-2 space-y-3">
            <h2 className="font-heading text-xl font-bold">Semua Repository ({repos.length})</h2>
            {repos.map((r) => (
              <div key={r.id} className="card p-4 flex items-center gap-3" data-testid={`admin-repo-${r.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex gap-2 flex-wrap">
                    <span className="chip chip-magenta text-[10px]">{r.category}</span>
                    <span className="chip chip-green text-[10px]">★ {r.stars?.toLocaleString() || 0}</span>
                  </div>
                  <h3 className="font-heading font-bold mt-1 truncate">{r.name} <span className="font-mono text-xs text-slate-500">({r.full_name})</span></h3>
                </div>
                <button onClick={() => syncRepo(r.id)} className="btn-ghost" data-testid={`sync-repo-${r.id}`}>
                  <RefreshCw className="w-4 h-4" />
                </button>
                <button onClick={() => deleteRepo(r.id)} className="btn-ghost hover:!border-red-500 hover:!text-red-400" data-testid={`delete-repo-${r.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "harvester" && (
        <div className="grid md:grid-cols-2 gap-6">
          <form onSubmit={generateAI} className="card p-6 space-y-4" data-testid="ai-form">
            <h2 className="font-heading text-xl font-bold flex items-center gap-2"><Sparkles className="w-5 h-5 text-magenta1" /> AI Article Generator</h2>
            <p className="text-slate-400 text-sm">Buat artikel berkualitas menggunakan GPT-5.4 tentang topik AI terkini.</p>
            <input required placeholder="Topik (e.g. Rilis DeepSeek V3 dan dampaknya)" value={aiTopic} onChange={(e) => setAiTopic(e.target.value)} className="w-full bg-void border border-white/10 rounded-lg h-11 px-3 outline-none" data-testid="ai-topic-input" />
            <select value={aiCategory} onChange={(e) => setAiCategory(e.target.value)} className="w-full bg-void border border-white/10 rounded-lg h-11 px-3 outline-none">
              <option>AI</option><option>Robotics</option><option>LLM</option><option>Software</option>
            </select>
            <button disabled={busy} className="btn-primary w-full justify-center" data-testid="ai-generate-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Generate Artikel
            </button>
          </form>
          <div className="card p-6 space-y-4">
            <h2 className="font-heading text-xl font-bold flex items-center gap-2"><Rss className="w-5 h-5 text-cyan1" /> RSS Harvester</h2>
            <p className="text-slate-400 text-sm">Fetch artikel dari TechCrunch AI, The Verge AI, IEEE Spectrum Robotics, dan AI News.</p>
            <div className="flex gap-3">
              <input type="number" min="1" max="20" value={rssLimit} onChange={(e) => setRssLimit(e.target.value)} className="w-24 bg-void border border-white/10 rounded-lg h-11 px-3 outline-none text-center" data-testid="rss-limit" />
              <button onClick={fetchRSS} disabled={busy} className="btn-primary flex-1 justify-center" data-testid="rss-fetch-submit">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rss className="w-4 h-4" />} Fetch RSS Sekarang
              </button>
            </div>
            <div className="text-xs text-slate-500 font-mono">
              Sumber:<br />
              · techcrunch.com/category/artificial-intelligence<br />
              · theverge.com/ai-artificial-intelligence<br />
              · spectrum.ieee.org/robotics<br />
              · artificialintelligence-news.com
            </div>
          </div>
        </div>
      )}

      {tab === "comments" && (
        <div className="space-y-4" data-testid="admin-comments-panel">
          <h2 className="font-heading text-xl font-bold flex items-center gap-2"><MessageSquare className="w-5 h-5 text-cyan1" /> Moderasi Komentar ({comments.length})</h2>
          {comments.length === 0 && <p className="text-slate-600 text-sm">Belum ada komentar.</p>}
          {comments.map((c) => (
            <div key={c.id} className={`card p-4 flex gap-3 items-start ${c.status === "hidden" ? "opacity-50" : ""}`} data-testid={`admin-comment-${c.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap text-xs font-mono text-slate-500">
                  <span className="font-heading font-bold text-sm text-white">{c.user_name}</span>
                  <span>· {new Date(c.created_at).toLocaleString("id-ID")}</span>
                  {c.flagged && <span className="chip chip-magenta text-[10px]">PERLU REVIEW (moderasi AI gagal)</span>}
                  {c.status === "hidden" && <span className="chip text-[10px]">DISEMBUNYIKAN</span>}
                </div>
                <p className="text-sm text-slate-300 mt-1 break-words">{c.content}</p>
                <p className="text-[11px] text-slate-600 mt-1 truncate">Artikel: {c.article_title}</p>
              </div>
              <button onClick={() => toggleCommentStatus(c)} className="btn-ghost !px-3 !py-2" title={c.status === "visible" ? "Sembunyikan" : "Tampilkan"} data-testid={`toggle-comment-${c.id}`}>
                {c.status === "visible" ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
              <button onClick={() => deleteComment(c.id)} className="btn-ghost !px-3 !py-2 hover:!text-red-400" data-testid={`admin-delete-comment-${c.id}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {tab === "videos" && <VideoAdmin />}

      {tab === "payments" && (
        <div className="grid lg:grid-cols-2 gap-6" data-testid="admin-payments-panel">
          <div className="space-y-6">
            {paySettings && (
              <form onSubmit={savePaymentSettings} className="card p-6 space-y-4" data-testid="payment-settings-form">
                <h2 className="font-heading text-xl font-bold flex items-center gap-2"><CreditCard className="w-5 h-5 text-cyan1" /> Pengaturan Pembayaran</h2>
                <p className="text-slate-500 text-sm">Ubah harga/paket kapan pun ada masalah pada situs pembayaran. Nominal ditentukan server (aman dari manipulasi).</p>
                <div>
                  <label className="text-xs font-mono uppercase tracking-widest text-slate-500">Nama Paket</label>
                  <input value={paySettings.plan_name} onChange={(e) => setPaySettings({ ...paySettings, plan_name: e.target.value })} className="mt-1 w-full bg-void border border-white/10 rounded-lg h-10 px-3 outline-none" data-testid="plan-name-input" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-mono uppercase tracking-widest text-slate-500">Harga Kartu (USD / bulan)</label>
                    <input type="number" min={1} max={1000} step="0.5" value={paySettings.price_usd} onChange={(e) => setPaySettings({ ...paySettings, price_usd: parseFloat(e.target.value) || 1 })} className="mt-1 w-full bg-void border border-white/10 rounded-lg h-10 px-3 outline-none" data-testid="price-input" />
                  </div>
                  <div>
                    <label className="text-xs font-mono uppercase tracking-widest text-slate-500">Harga QRIS (IDR / bulan)</label>
                    <input type="number" min={10000} max={10000000} step="1000" value={paySettings.price_idr} onChange={(e) => setPaySettings({ ...paySettings, price_idr: parseInt(e.target.value, 10) || 10000 })} className="mt-1 w-full bg-void border border-white/10 rounded-lg h-10 px-3 outline-none" data-testid="price-idr-input" />
                  </div>
                </div>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" checked={paySettings.payments_enabled} onChange={(e) => setPaySettings({ ...paySettings, payments_enabled: e.target.checked })} className="w-4 h-4 accent-cyan-400" data-testid="payments-enabled-toggle" />
                  <span className="text-sm text-slate-300">Pembayaran aktif (matikan saat ada gangguan)</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" checked={paySettings.qris_enabled} onChange={(e) => setPaySettings({ ...paySettings, qris_enabled: e.target.checked })} className="w-4 h-4 accent-cyan-400" data-testid="qris-enabled-toggle" />
                  <span className="text-sm text-slate-300">Terima QRIS (GoPay/OVO/DANA/bank) via Midtrans</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" checked={paySettings.crypto_enabled} onChange={(e) => setPaySettings({ ...paySettings, crypto_enabled: e.target.checked })} className="w-4 h-4 accent-cyan-400" data-testid="crypto-enabled-toggle" />
                  <span className="text-sm text-slate-300">Terima crypto (USDC) via Stripe — perlu akun Stripe US saat live</span>
                </label>
                <button disabled={busy} className="btn-primary w-full justify-center" data-testid="payment-settings-submit">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Simpan
                </button>
              </form>
            )}
            <div className="card p-6" data-testid="digest-card">
              <h2 className="font-heading text-xl font-bold flex items-center gap-2 mb-2"><Mail className="w-5 h-5 text-magenta1" /> Digest Mingguan &amp; Email</h2>
              <p className="text-slate-500 text-sm mb-4">Digest dibuat otomatis tiap minggu oleh AI dan dikirim ke pelanggan via Resend.</p>
              {emailStatus && (
                <div className={`text-xs font-mono mb-4 flex items-center gap-2 ${emailStatus.configured ? "text-green-400" : "text-amber-400"}`} data-testid="email-status">
                  <span className={`w-2 h-2 rounded-full ${emailStatus.configured ? "bg-green-400" : "bg-amber-400"}`} />
                  {emailStatus.configured ? `Email aktif · pengirim ${emailStatus.sender}` : "Email belum dikonfigurasi (RESEND_API_KEY)"}
                </div>
              )}
              {paySettings && (
                <>
                  <label className="flex items-center gap-3 cursor-pointer mb-2">
                    <input type="checkbox" checked={paySettings.auto_email_digest} onChange={(e) => toggleSetting("auto_email_digest", e.target.checked, "Auto-kirim digest")} className="w-4 h-4 accent-cyan-400" data-testid="auto-email-toggle" />
                    <span className="text-sm text-slate-300">Kirim otomatis ke pelanggan saat digest mingguan terbit</span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer mb-4">
                    <input type="checkbox" checked={paySettings.notify_new_articles} onChange={(e) => toggleSetting("notify_new_articles", e.target.checked, "Notifikasi artikel baru")} className="w-4 h-4 accent-cyan-400" data-testid="notify-articles-toggle" />
                    <span className="text-sm text-slate-300">Kirim notifikasi singkat setiap ada berita baru (setelah ringkasan AI selesai)</span>
                  </label>
                  <form onSubmit={saveEmailSettings} className="grid sm:grid-cols-2 gap-3 mb-4" data-testid="email-settings-form">
                    <div>
                      <label className="text-xs font-mono uppercase tracking-widest text-slate-500">URL Situs (link di email)</label>
                      <input placeholder={window.location.origin} value={paySettings.site_url || ""} onChange={(e) => setPaySettings({ ...paySettings, site_url: e.target.value })} className="mt-1 w-full bg-void border border-white/10 rounded-lg h-10 px-3 outline-none text-sm" data-testid="site-url-input" />
                    </div>
                    <div>
                      <label className="text-xs font-mono uppercase tracking-widest text-slate-500">Email Pengirim (domain terverifikasi)</label>
                      <input placeholder="newsletter@domain-anda.com" value={paySettings.sender_email || ""} onChange={(e) => setPaySettings({ ...paySettings, sender_email: e.target.value })} className="mt-1 w-full bg-void border border-white/10 rounded-lg h-10 px-3 outline-none text-sm" data-testid="sender-email-input" />
                    </div>
                    <button disabled={busy} className="btn-ghost sm:col-span-2 justify-center" data-testid="email-settings-submit"><Save className="w-4 h-4" /> Simpan Pengaturan Email</button>
                  </form>
                </>
              )}
              <div className="grid sm:grid-cols-2 gap-3">
                <button onClick={generateDigest} disabled={busy} className="btn-primary justify-center" data-testid="generate-digest-btn">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Buat Digest Sekarang
                </button>
                <button onClick={notifyLatest} disabled={busy || !emailStatus?.configured} className="btn-ghost justify-center" data-testid="notify-latest-btn">
                  <Mail className="w-4 h-4" /> Kirim Notif Artikel Terbaru
                </button>
              </div>
              <div className="flex flex-wrap gap-2 mt-3 text-xs">
                <span className="text-slate-500 font-mono uppercase tracking-widest self-center">Pratinjau:</span>
                <button onClick={() => openPreview({ type: "articles", limit: 5 })} className="btn-ghost !py-1 !px-3" data-testid="preview-articles-btn"><Eye className="w-3 h-3" /> Notif Artikel</button>
                <button onClick={() => openPreview({ type: "digest" })} className="btn-ghost !py-1 !px-3" data-testid="preview-digest-btn"><Eye className="w-3 h-3" /> Digest Terbaru</button>
                <button onClick={() => openPreview({ type: "welcome" })} className="btn-ghost !py-1 !px-3" data-testid="preview-welcome-btn"><Eye className="w-3 h-3" /> Selamat Datang</button>
              </div>
              {emailStatus?.opted_out > 0 && (
                <p className="text-[11px] font-mono text-slate-500 mt-3" data-testid="opted-out-count">{emailStatus.opted_out} pelanggan memilih berhenti menerima email</p>
              )}
              <div className="mt-4 space-y-2 max-h-52 overflow-y-auto" data-testid="admin-digest-list">
                {digests.map((d) => (
                  <div key={d.id} className="flex items-center justify-between gap-2 text-xs font-mono border-b border-white/5 pb-2">
                    <div className="min-w-0">
                      <div className="text-slate-300 truncate">{d.week} · {d.title}</div>
                      <div className="text-slate-600">{d.emailed_at ? `Terkirim ${d.email_sent} email` : "Belum dikirim"}</div>
                    </div>
                    <button onClick={() => openPreview({ type: "digest", id: d.id })} className="btn-ghost !py-1 !px-2 shrink-0" title="Pratinjau" data-testid={`preview-digest-${d.id}`}>
                      <Eye className="w-3 h-3" />
                    </button>
                    <button onClick={() => sendDigest(d.id)} disabled={busy || !emailStatus?.configured} className="btn-ghost !py-1 !px-3 shrink-0" data-testid={`send-digest-${d.id}`}>
                      <Mail className="w-3 h-3" /> Kirim
                    </button>
                  </div>
                ))}
              </div>
              <form onSubmit={sendTestEmail} className="mt-4 flex gap-2">
                <input type="email" required placeholder="Kirim email tes ke..." value={testEmail} onChange={(e) => setTestEmail(e.target.value)} className="flex-1 bg-void border border-white/10 rounded-lg h-10 px-3 outline-none text-sm" data-testid="test-email-input" />
                <button disabled={busy || !emailStatus?.configured} className="btn-ghost" data-testid="test-email-btn">Tes</button>
              </form>
            </div>
          </div>

          <div className="space-y-6">
            <div className="card p-6" data-testid="subscribers-card">
              <h2 className="font-heading text-xl font-bold mb-3">Pelanggan Aktif ({subscribers.filter((s) => s.status === "active").length})</h2>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {subscribers.length === 0 && <p className="text-slate-600 text-sm">Belum ada pelanggan.</p>}
                {subscribers.map((s) => (
                  <div key={s.user_id} className="flex items-center justify-between text-sm font-mono">
                    <span className="text-slate-300 truncate">{s.email}</span>
                    <span className={`chip text-[10px] ${s.status === "active" ? "chip-green" : ""}`}>{s.status}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="card p-6" data-testid="transactions-card">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-heading text-xl font-bold">Riwayat Transaksi</h2>
                <button onClick={reconcileQris} disabled={busy} className="btn-ghost !py-1 !px-3 text-xs" data-testid="reconcile-qris-btn"><RefreshCw className="w-3 h-3" /> Cek QRIS</button>
              </div>
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {transactions.length === 0 && <p className="text-slate-600 text-sm">Belum ada transaksi.</p>}
                {transactions.map((t) => (
                  <div key={t.session_id} className="flex items-center justify-between text-xs font-mono border-b border-white/5 pb-2">
                    <div className="min-w-0">
                      <div className="text-slate-300 truncate">{t.email}</div>
                      <div className="text-slate-600">{new Date(t.created_at).toLocaleString("id-ID")} · {t.provider === "midtrans_qris" ? "QRIS" : "Stripe"}</div>
                    </div>
                    <div className="text-right shrink-0 ml-2">
                      <div className="text-white">{t.currency === "idr" ? `Rp${Number(t.amount).toLocaleString("id-ID")}` : `$${t.amount}`}</div>
                      <span className={`chip text-[10px] ${t.payment_status === "paid" ? "chip-green" : t.payment_status === "pending" ? "chip-cyan" : "chip-magenta"}`}>{t.payment_status}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === "security" && (
        <div className="grid lg:grid-cols-2 gap-6">
          <form onSubmit={changePassword} className="card p-6 space-y-4" data-testid="change-password-form">
            <h2 className="font-heading text-xl font-bold flex items-center gap-2"><KeyRound className="w-5 h-5 text-cyan1" /> Ganti Password Admin</h2>
            <p className="text-slate-400 text-sm">Perbarui password login admin. Minimal 8 karakter.</p>
            <input required type="password" placeholder="Password saat ini" value={pwForm.current} onChange={(e) => setPwForm({ ...pwForm, current: e.target.value })} className="w-full bg-void border border-white/10 rounded-lg h-11 px-3 focus:border-cyan1/50 outline-none" data-testid="current-password-input" />
            <input required type="password" minLength={8} placeholder="Password baru (min. 8 karakter)" value={pwForm.next} onChange={(e) => setPwForm({ ...pwForm, next: e.target.value })} className="w-full bg-void border border-white/10 rounded-lg h-11 px-3 focus:border-cyan1/50 outline-none" data-testid="new-password-input" />
            <input required type="password" minLength={8} placeholder="Konfirmasi password baru" value={pwForm.confirm} onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })} className="w-full bg-void border border-white/10 rounded-lg h-11 px-3 focus:border-cyan1/50 outline-none" data-testid="confirm-password-input" />
            <button disabled={busy} className="btn-primary w-full justify-center" data-testid="change-password-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />} Ubah Password
            </button>
          </form>

          <div className="space-y-6">
            {secSettings && (
              <form onSubmit={saveSecuritySettings} className="card p-6 space-y-4" data-testid="security-settings-form">
                <h2 className="font-heading text-xl font-bold flex items-center gap-2"><Shield className="w-5 h-5 text-magenta1" /> Pengaturan Keamanan &amp; Auto Update</h2>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-mono uppercase tracking-widest text-slate-500">Token Expiry (hari)</label>
                    <input type="number" min={1} max={30} value={secSettings.token_expiry_days} onChange={(e) => setSecSettings({ ...secSettings, token_expiry_days: parseInt(e.target.value) || 1 })} className="mt-1 w-full bg-void border border-white/10 rounded-lg h-10 px-3 outline-none" data-testid="token-expiry-input" />
                  </div>
                  <div>
                    <label className="text-xs font-mono uppercase tracking-widest text-slate-500">Max Percobaan Login</label>
                    <input type="number" min={3} max={10} value={secSettings.max_login_attempts} onChange={(e) => setSecSettings({ ...secSettings, max_login_attempts: parseInt(e.target.value) || 3 })} className="mt-1 w-full bg-void border border-white/10 rounded-lg h-10 px-3 outline-none" data-testid="max-attempts-input" />
                  </div>
                  <div>
                    <label className="text-xs font-mono uppercase tracking-widest text-slate-500">Durasi Lockout (menit)</label>
                    <input type="number" min={5} max={120} value={secSettings.lockout_minutes} onChange={(e) => setSecSettings({ ...secSettings, lockout_minutes: parseInt(e.target.value) || 5 })} className="mt-1 w-full bg-void border border-white/10 rounded-lg h-10 px-3 outline-none" data-testid="lockout-input" />
                  </div>
                  <div>
                    <label className="text-xs font-mono uppercase tracking-widest text-slate-500">Interval Auto Update</label>
                    <select value={secSettings.auto_update_interval_hours} onChange={(e) => setSecSettings({ ...secSettings, auto_update_interval_hours: parseInt(e.target.value) })} className="mt-1 w-full bg-void border border-white/10 rounded-lg h-10 px-3 outline-none" data-testid="interval-select">
                      <option value={1}>Setiap 1 jam</option>
                      <option value={3}>Setiap 3 jam</option>
                      <option value={6}>Setiap 6 jam</option>
                      <option value={12}>Setiap 12 jam</option>
                      <option value={24}>Setiap 24 jam</option>
                    </select>
                  </div>
                </div>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" checked={secSettings.auto_update_enabled} onChange={(e) => setSecSettings({ ...secSettings, auto_update_enabled: e.target.checked })} className="w-4 h-4 accent-cyan-400" data-testid="auto-update-toggle" />
                  <span className="text-sm text-slate-300">Auto update RSS &amp; GitHub aktif</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" checked={secSettings.ai_summary_enabled} onChange={(e) => setSecSettings({ ...secSettings, ai_summary_enabled: e.target.checked })} className="w-4 h-4 accent-cyan-400" data-testid="ai-summary-toggle" />
                  <span className="text-sm text-slate-300">Ringkasan AI otomatis untuk artikel RSS baru</span>
                </label>
                <button disabled={busy} className="btn-primary w-full justify-center" data-testid="security-settings-submit">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Simpan Pengaturan
                </button>
              </form>
            )}

            {syncStatus && (
              <div className="card p-6 space-y-2" data-testid="sync-status-card">
                <h2 className="font-heading text-xl font-bold flex items-center gap-2"><RefreshCw className="w-5 h-5 text-cyan1" /> Status Auto Update</h2>
                <div className="text-sm text-slate-400 font-mono space-y-1">
                  <div>Terakhir jalan: <span className="text-cyan1">{syncStatus.last_run ? new Date(syncStatus.last_run).toLocaleString("id-ID") : "-"}</span></div>
                  <div>Artikel RSS baru: <span className="text-white">{syncStatus.rss_inserted ?? 0}</span></div>
                  <div>Repo di-sync: <span className="text-white">{syncStatus.repos_updated ?? 0}</span></div>
                  <div>Trigger: <span className="text-magenta1">{syncStatus.trigger || "manual"}</span></div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {preview && <EmailPreviewModal preview={preview} onClose={() => setPreview(null)} />}
    </div>
  );
}
